import React, { useEffect, useContext } from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { Button, Paper, TextField as MuiTextField } from '@material-ui/core';
import { spacing } from '@material-ui/system';
import { Alert as MuiAlert } from '@material-ui/lab';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { AuthContext } from '../../../../core/AuthContext';

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const Wrapper = styled(Paper)`
  padding: ${props => props.theme.spacing(6)}px;

  ${props => props.theme.breakpoints.up('md')} {
    padding: ${props => props.theme.spacing(10)}px;
  }
`;

interface PreAuthorisation {
  username: string;
}

interface IProps {}

const Login: React.FC<IProps> = ({}: IProps) => {
  const authenticationContext = useContext(AuthContext);

  const [authenticateWithJwtToken] = GQLOperationBuilder.security.authenticateWithJwtToken({
    onCompleted: loginResponse => {
      const authentication = GQLOperationBuilder.security.authenticationFromResponse(loginResponse);
      if (authentication) {
        authenticationContext.setAuthentication(authentication);
      }
    }
  });

  const [authenticateWithEmailAndPassword] = GQLOperationBuilder.security.authenticateWithEmailAndPassword({
    onCompleted: loginResponse => {
      const authentication = GQLOperationBuilder.security.authenticationFromResponse(loginResponse);
      if (authentication) {
        authenticationContext.setAuthentication(authentication);
      }
    }
  });

  const handleLogin = (values: any) => {
    authenticateWithEmailAndPassword({
      variables: { email: values.email, password: values.password }
    }).then();
  };

  useEffect(() => {
    if (window.location.search) {
      const query = window.location.search.replace('?', '').split('=');
      const authJwtTokenQueryParamValue = window.location.search.replace("?", '').split("&").find(b => b.indexOf("authJwtToken") !== -1)?.split("=")
      if (authJwtTokenQueryParamValue && authJwtTokenQueryParamValue.length > 0) {
        authenticateWithJwtToken({ variables: { token: authJwtTokenQueryParamValue[1] } }).then();
      } 
    } else {
      const jwtElement = document.getElementById('authJwtToken')
      if (jwtElement && jwtElement.innerText) {
        authenticateWithJwtToken({ variables: { token: jwtElement.innerText } }).then();
      }
    }
  }, [authenticateWithJwtToken]);

  const preAuthorisation: PreAuthorisation = sessionStorage.getItem('preAuthorisation')
    ? (JSON.parse(sessionStorage.getItem('preAuthorisation')!) as PreAuthorisation)
    : { username: 'producer@example.com' };

  return (
    <Wrapper>
      <Helmet title="Sign In" />
      <Formik
        initialValues={{
          email: preAuthorisation.username,
          password: '',
          submit: false
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email('Must be a valid email')
            .max(255)
            .required('Email is required'),
          password: Yup.string()
            .max(255)
            .required('Password is required')
        })}
        validateOnChange={true}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            sessionStorage.setItem('preAuthorisation', JSON.stringify({ username: values.email }));
            handleLogin(values);
          } catch (error) {
            const message = error.message || 'Something went wrong';

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert mt={2} mb={1} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              type="email"
              name="email"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            <TextField
              type="password"
              name="password"
              label="Password"
              value={values.password}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              style={{ marginTop: '16px' }}
            >
              Sign in
            </Button>
          </form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default Login;
